import { forwardRef, memo, useMemo, useState, useCallback, useEffect, useContext } from 'react';
import size from 'lodash/size';
import take from 'lodash/take';
import isBoolean from 'lodash/isBoolean';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
// Skillmore UI Components
import { MIN_PAGE_SIZE } from '@empathco/ui-components/src/config/params';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import OnOffSwitch from '@empathco/ui-components/src/elements/OnOffSwitch';
import ExportButton, { type ExportParams } from '@empathco/ui-components/src/elements/ExportButton';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import useCustomerSettings from '../config/customer';
import { API_SUPV_TEAM_EXPORT } from '../config/api';
import { PATH_SUPERVISOR_EMPLOYEE } from '../config/paths';
import { getSettingsBoolValue } from '../helpers/context';
import { GlobalContext } from '../context/global';
import { SupervisorContext } from '../context/supervisor';
import { DataContext } from '../context';
import EmployeeCard, { EmployeeCardItem, EmployeeCardProps } from '../v3/EmployeeCard';

const componentProps: Partial<EmployeeCardProps> = {
  route: PATH_SUPERVISOR_EMPLOYEE,
  withActivity: true
};

// eslint-disable-next-line complexity
const TeamView = forwardRef<HTMLDivElement>((_props, ref) => {
  const { HAS_REDUCED_UI } = useCustomerSettings();
  const { token } = useContext(GlobalContext);
  const {
    settings: { data: settingsData, pending: pendingSettings, failed: failedSettings },
    settingsUpdate: { pending: pendingSettingsUpdate }, updateSettings
  } = useContext(DataContext);
  const {
    mgrTeam: { data: team, pending: pendingTeam, failed: failedTeam }, requireTeam
  } = useContext(SupervisorContext);

  const settingsLoaded = pendingSettings === false && failedSettings === false && Boolean(settingsData);
  const settings = settingsLoaded ? settingsData : null;
  const settingsId = 'team_view' as const;
  const settingsEmployeesOnly = HAS_REDUCED_UI ? getSettingsBoolValue(settings, `${settingsId}__employees_only`) : false;
  const settingsUnfolded = getSettingsBoolValue(settings, `${settingsId}__unfolded`);

  const [employeesOnly, setEmployeesOnly] = useState<boolean | undefined>(settingsEmployeesOnly);
  const [unfolded, setUnfolded] = useState(Boolean(settingsUnfolded));

  const handleEmployeesOnly = useCallback((value: boolean) => {
    const val = Boolean(value);
    setEmployeesOnly(val);
    if (Boolean(settingsEmployeesOnly) !== val) updateSettings?.({ [`${settingsId}__employees_only`]: val });
  }, [settingsId, settingsEmployeesOnly, updateSettings]);

  const handleUnfolding = useCallback(() => {
    const val = !unfolded;
    setUnfolded(val);
    if (Boolean(settingsUnfolded) !== val) updateSettings?.({ [`${settingsId}__unfolded`]: val });
  }, [settingsId, settingsUnfolded, unfolded, updateSettings]);

  useEffect(() => {
    if (settingsLoaded && !pendingSettingsUpdate) {
      if (HAS_REDUCED_UI) setEmployeesOnly(Boolean(getSettingsBoolValue(settings, `${settingsId}__employees_only`)));
      setUnfolded(Boolean(getSettingsBoolValue(settings, `${settingsId}__unfolded`)));
    }
  }, [settingsLoaded, pendingSettingsUpdate, settings, settingsId, HAS_REDUCED_UI]);

  useEffect(() => {
    if (isBoolean(employeesOnly)) requireTeam?.({ employees_only: employeesOnly });
  }, [employeesOnly, requireTeam]);

  const exportParams = useMemo(() => token ? { token: token as string } as ExportParams : undefined, [token]);
  const showLess = useMemo(() => team ? take(team, MIN_PAGE_SIZE) : undefined, [team]);

  const showMembers = unfolded ? team : showLess;
  const count = size(showMembers);
  const teamCount = size(team);

  const pending = !settingsLoaded || pendingTeam;
  const failed = failedTeam || failedSettings;
  const disabled = pending || failed || !team || pendingSettingsUpdate;

  return (
    <CardsGrid
        ref={ref}
        variant="shady"
        items={showMembers as (EmployeeCardItem[] | null | undefined)}
        pending={pending}
        failed={failed}
        withoutTopPadding
        blendFilters
        blendPagination
        component={EmployeeCard}
        ComponentProps={componentProps}
        notFoundMessage="employees.no_employees"
        filters={(
          <Box
              flexGrow={1}
              display="flex"
              flexWrap="wrap"
              alignItems="flex-start"
              justifyContent="flex-end"
          >
            {HAS_REDUCED_UI && settingsLoaded && !failed ? (
              <Box pl={2} py={1} display="flex" justifyContent="flex-start">
                <OnOffSwitch
                    label="team.employees_only"
                    value={Boolean(employeesOnly)}
                    onChange={handleEmployeesOnly}
                    disabled={disabled ? true : undefined}
                />
              </Box>
            ) : undefined}
            <ExportButton
                title="supervisor.my_team.export"
                params={exportParams}
                endpoint={API_SUPV_TEAM_EXPORT}
                disabled={disabled || count < 1}
            />
          </Box>
        )}
        pagination={!pending && !failed && count >= 1 ? (
          <Box flexGrow={1} display="flex" justifyContent="flex-end">
            <FormattedMessage
                id={teamCount > MIN_PAGE_SIZE ? 'team.fold' : 'team.showing_all'}
                values={{
                  showing: count,
                  all: teamCount,
                  link: (
                    <StandardLink
                        text={unfolded ? 'common.show_less' : 'common.show_more'}
                        onClick={disabled ? undefined : handleUnfolding}
                    />
                  )
                }}
            />
          </Box>
        ) : undefined}
    />
  );
});

TeamView.displayName = 'TeamView';

export default memo(TeamView);
